
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import * as echarts from 'echarts';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import {PermissionsGroup} from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import IncludeUnappliedToggle from '@/components/IncludeUnappliedToggle.vue';
import settingx from '@/store/modules/settingx';
import { COL, getTableName, TIMEZONE_OFFSET_MS } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		StackedBarChartFilterable,
		IncludeUnappliedToggle,
	},
})
export default class CustomerAgingSummaryBarChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['customers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['customers', 'agents', 'dateAsOf'];

	public result: number[] = [];
	public xAxisData: string[] = [];
	public series: echarts.ECharts[] | any = [];

	public get currencySymbol() {
		return gdbx.currencySymbol;
	}

	public get dateFormatted() {
		return [this.selectedAsOfDate];
	}

	public get includeUnapplied() {
		return settingx.now.includeUnapplied;
	}

	public get expensiveHook() {
		const { selectedAsOfDate, selectedAgents, selectedCustomers, includeUnapplied } = this;
		return JSON.stringify([
			selectedAsOfDate,
			selectedAgents,
			selectedCustomers,
			includeUnapplied,
		]);
	}

	public async expensiveCalc() {
		this.generateLoadingText(0.2, 0.3);

		const asOf = this.selectedAsOfDate;
		const msPerDay = 24 * 60 * 60 * 1000;

		// Build aging bucket CASE WHEN expressions
		const buildAgingBuckets = (dateCol: string, amountCol: string) => {
			const daysAgo = `((${asOf} - TRY_CAST(${dateCol} AS BIGINT)) / ${msPerDay})`;
			const amt = `TRY_CAST(${amountCol} AS DOUBLE)`;
			return `
				SUM(CASE WHEN ${daysAgo} >= 0 AND ${daysAgo} < 30 THEN ${amt} ELSE 0 END) as b0,
				SUM(CASE WHEN ${daysAgo} >= 30 AND ${daysAgo} < 60 THEN ${amt} ELSE 0 END) as b30,
				SUM(CASE WHEN ${daysAgo} >= 60 AND ${daysAgo} < 90 THEN ${amt} ELSE 0 END) as b60,
				SUM(CASE WHEN ${daysAgo} >= 90 AND ${daysAgo} < 120 THEN ${amt} ELSE 0 END) as b90,
				SUM(CASE WHEN ${daysAgo} >= 120 THEN ${amt} ELSE 0 END) as b120
			`;
		};

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		let customerFilter = '';
		if (this.selectedCustomers.length > 0) {
			const list = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
			customerFilter = ` AND "CODE" IN (${list})`;
		}
		let agentFilter = '';
		if (this.selectedAgents.length > 0) {
			const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND "AGENT" IN (${list})`;
		}

		const ivdnTable = getTableName('customerIvDns');
		const koTable = getTableName('customerKos');
		const tradeTable = getTableName('customerTrades');

		const jobId = this.currentJobId;
		const queryHead = await this._ensureTablesLoaded(['customerIvDns', 'customerKos', 'customerTrades']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.9);

		// Run all queries in parallel - group by customer code for FIFO processing
		const [ivdnData, koData, gainLossData, unappliedData] = await Promise.all([
			queryHead.rawQuery(`
				SELECT ${COL.CODE} as code,
					${buildAgingBuckets(COL.DOCDATE, COL.LOCALDOCAMT)}
				FROM ${ivdnTable}
				WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) <= ${asOf}
					${customerFilter}${agentFilter}
				GROUP BY ${COL.CODE}
			`),
			queryHead.rawQuery(`
				SELECT ${COL.CODE} as code,
					${buildAgingBuckets('"TODOCDATE"', COL.KOLOCALAMT)}
				FROM ${koTable}
				WHERE TRY_CAST("TODOCDATE" AS BIGINT) <= ${asOf}
					AND TRY_CAST("FROMDOCDATE" AS BIGINT) <= ${asOf}
					${customerFilter}${agentFilter}
				GROUP BY ${COL.CODE}
			`),
			queryHead.rawQuery(`
				SELECT ${COL.CODE} as code,
					${buildAgingBuckets('"TODOCDATE"', COL.KOGAINLOSS)}
				FROM ${koTable}
				WHERE TRY_CAST("TODOCDATE" AS BIGINT) <= ${asOf}
					AND TRY_CAST("FROMDOCDATE" AS BIGINT) <= ${asOf}
					${customerFilter}${agentFilter}
				GROUP BY ${COL.CODE}
			`),
			queryHead.rawQuery(`
				SELECT ${COL.CODE} as code,
					SUM(TRY_CAST(${COL.UNAPPLIEDAMT} AS DOUBLE)) as unapplied
				FROM ${tradeTable}
				WHERE TRY_CAST(${COL.DOCDATE} AS BIGINT) <= ${asOf}
					${customerFilter}${agentFilter}
				GROUP BY ${COL.CODE}
			`),
		]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.95);

		// Build lookup maps by customer code
		const koMap = new Map<string, number[]>();
		for (const row of koData) {
			koMap.set(String(row.code), [
				Number(row.b0) || 0, Number(row.b30) || 0, Number(row.b60) || 0,
				Number(row.b90) || 0, Number(row.b120) || 0,
			]);
		}

		const gainLossMap = new Map<string, number[]>();
		for (const row of gainLossData) {
			gainLossMap.set(String(row.code), [
				Number(row.b0) || 0, Number(row.b30) || 0, Number(row.b60) || 0,
				Number(row.b90) || 0, Number(row.b120) || 0,
			]);
		}

		const unappliedMap = new Map<string, number>();
		for (const row of unappliedData) {
			unappliedMap.set(String(row.code), Number(row.unapplied) || 0);
		}

		// Process each customer: ivdn - ko - gainLoss, then apply FIFO for unapplied
		const periodTotals = [0, 0, 0, 0, 0];
		const periodLabels = ['Current', '> 30 Days', '> 60 Days', '> 90 Days', '> 120 Days'];

		for (const row of ivdnData) {
			const code = String(row.code);
			const ivdnBuckets = [
				Number(row.b0) || 0, Number(row.b30) || 0, Number(row.b60) || 0,
				Number(row.b90) || 0, Number(row.b120) || 0,
			];
			const koBuckets = koMap.get(code) || [0, 0, 0, 0, 0];
			const gainLossBuckets = gainLossMap.get(code) || [0, 0, 0, 0, 0];

			// Calculate net amounts per bucket
			const netBuckets = ivdnBuckets.map((v, i) => v - koBuckets[i] - gainLossBuckets[i]);

			// Apply FIFO for unapplied if enabled (from oldest to newest)
			if (this.includeUnapplied) {
				let remaining = unappliedMap.get(code) || 0;
				for (let i = 4; i >= 0 && remaining > 0; i--) {
					const deduction = Math.min(netBuckets[i], remaining);
					netBuckets[i] -= deduction;
					remaining -= deduction;
				}
			}

			// Add to totals
			for (let i = 0; i < 5; i++) {
				periodTotals[i] += netBuckets[i];
			}
		}

		this.xAxisData = periodLabels;
		this.result = periodTotals;

		this.series = [
			{
				name: 'Total',
				data: this.result,
				type: 'bar',
			},
		];

		this.generateLoadingText(1);
		this.saveHistory('series', 'xAxisData');
	}
}
